import { useState } from 'react';
import { NextPageContext } from 'next';
import InsightWrapper from './insight.style';
import { LAWTALK_INSIGHT } from '@shared/values';
import HttpCommon from '@utils/http-common';
import InsightService from '@service/insightService';
import InsightsListType from '../../../types/insights';
import QuillViewer from '@component/QuillViewer';
import Link from 'next/link';
import InsightList from '@component/InsightList';
import Facebook from '../../../public/icon/Facebook';
import Kakaotalk from '../../../public/icon/Kakaotalk';
import HeadMetaTag from '@component/HeadMetaTags';
import Alert from '@component/Alert';
import { MainLayout } from '@component/LayoutContainer';

type insightDetailType = {
  contents: string;
  buttonContent?: string;
  buttonUrl?: string;
  insightTags?: { id: number; name: string }[];
  joinInsightContents?: InsightsListType[];
};
type InsightType = {
  insight: InsightsListType & insightDetailType;
  isMobile: boolean;
  hostPath: string;
  metaDescription: string;
};

const initInsightData = {
  id: 0,
  title: '',
  titleImageUrl: '',
  tags: [],
  contents: '',
  buttonContent: '',
};

const Insight = ({ isMobile, insight = initInsightData, hostPath = '', metaDescription = '' }: InsightType) => {
  const alertResetdata = { open: false, title: '', description: '' };
  const [visibleAlert, setVisibleAlert] = useState(alertResetdata);
  const clickEvent = (url: string) => {
    window.open(`https://${url}`);
  };

  const copyInsightUrl = () => {
    try {
      const tempElem = document.createElement('textarea');
      tempElem.value = window.document.location.href;
      document.body.appendChild(tempElem);

      tempElem.select();
      document.execCommand('copy');
      document.body.removeChild(tempElem);
      document.body.classList.add('body-scroll-ban');
      setVisibleAlert({ open: true, title: '로톡인사이트 공유하기', description: 'URL이 복사되었습니다.' });
    } catch (error) {
      document.body.classList.add('body-scroll-ban');
      setVisibleAlert({ open: true, title: '로톡인사이트 공유하기', description: 'URL 복사를 실패하였습니다.' });
    }
  };

  const clickConfirm = () => {
    setVisibleAlert(alertResetdata);
    document.body.classList.remove('body-scroll-ban');
  };
  const shareKakaotalk = () => {
    // @ts-ignore: Unreachable code error
    const KakaoSdk = Kakao;
    const appKey = process.env.NEXT_PUBLIC_KAKAO_APP_KEY;
    if (!KakaoSdk.Link) {
      KakaoSdk.init(appKey);
    }
    if (KakaoSdk.Link) {
      KakaoSdk.Link.sendDefault({
        objectType: 'feed',
        content: {
          title: insight.title,
          description: metaDescription,
          imageUrl: insight.titleImageUrl,
          imageWidth: 600,
          imageHeight: 315,
          link: {
            mobileWebUrl: hostPath,
            webUrl: hostPath,
          },
        },
        buttons: [
          {
            title: '상세보기',
            link: {
              mobileWebUrl: hostPath,
              webUrl: hostPath,
            },
          },
        ],
      });
    }
  };
  return (
    <MainLayout>
      <InsightWrapper className="page-layout">
        <HeadMetaTag title={insight.title} description={metaDescription} image={insight.titleImageUrl} url={hostPath} />
        <h1 className="page-title mobile">{LAWTALK_INSIGHT}</h1>
        <div className="insightdetail--header">
          <p className="insightdetail--header-title">{insight.title}</p>
          <div className="insightdetail--header-tags">
            {insight.insightTags?.map((tag) => (
              <p className="insightdetail--header-tag" key={tag.id}>
                #{tag.name}
              </p>
            ))}
          </div>
        </div>
        <div className="insightdetail--body">
          <QuillViewer body={insight.contents} />
          {insight.buttonUrl && insight.buttonContent && (
            <button className="insightdetail--button" onClick={() => clickEvent(insight.buttonUrl || '')}>
              {insight.buttonContent}
            </button>
          )}
          <div className="insightdetail--body-footer">
            <div className="insightdetail--shares">
              <button className="insightdetail--shares-item-circle url" onClick={copyInsightUrl}>
                URL
              </button>
              <div className="fb-share-button" data-href={hostPath} data-layout="button_count">
                <button
                  className="fb-xfbml-parse-ignore insightdetail--shares-item-circle"
                  onClick={() => {
                    window.open(
                      `https://www.facebook.com/sharer/sharer.php?u=${hostPath}`,
                      '',
                      'scrollbars=no, width=600, height=600',
                    );
                    return;
                  }}
                >
                  <Facebook />
                </button>
              </div>
              <button id="kakao-link-btn" className="insightdetail--shares-item" onClick={shareKakaotalk}>
                <Kakaotalk />
              </button>
            </div>
            <Link href="/insight">
              <a className="insightdetail--list">목록 가기</a>
            </Link>
          </div>
        </div>
        {insight.joinInsightContents && insight.joinInsightContents.length > 0 && (
          <div className="insightdetail--relations">
            <div className="insightdetail--relations-content">
              <p className="insightdetail--relations-title">변호사님이 눈여겨 볼 콘텐츠</p>
              <div className="insightdetail--relations-body">
                <InsightList
                  insightsTotalCount={insight.joinInsightContents.length}
                  insights={insight.joinInsightContents}
                  isMobile={isMobile}
                  visiblePagination={false}
                />
              </div>
            </div>
          </div>
        )}
        {visibleAlert.open && (
          <div id="insightdetail--copy-alert">
            <Alert title={visibleAlert.title} description={visibleAlert.description} clickConfirm={clickConfirm} />
          </div>
        )}
      </InsightWrapper>
    </MainLayout>
  );
};

function absoluteUrl(req: any) {
  var protocol = 'https:';
  var host = req ? req.headers.host : window.location.hostname;
  if (host.indexOf('localhost') > -1) {
    protocol = 'http:';
  }

  return {
    protocol: protocol,
    host: host,
  };
}

Insight.getInitialProps = async ({ req, query, asPath }: NextPageContext) => {
  const insightId = query.insightId?.toString() || '';
  if (!HttpCommon.isAvailableUrl() || insightId.length === 0) {
    return;
  }

  try {
    const detailData = await InsightService.getDetail(insightId);
    const { protocol, host } = absoluteUrl(req);
    const URL = `${protocol}//${host}`;
    const hostPath = `${URL}${asPath}`;

    const metaDescription =
      String(detailData.data.contents)
        .replace(/<[^>]+>|&[\w]+;/gm, '')
        .slice(0, 155) || '';

    const detailResult = detailData.data;
    if (detailResult.joinInsightContents.length > 0) {
      detailResult.joinInsightContents = detailResult.joinInsightContents.map((join: any) => {
        join.tags = join.insightTags.map((tags: any) => tags.name);
        return join;
      });
    }
    return { insight: detailResult, hostPath, metaDescription };
  } catch (error) {
    console.error('insight detail getInitialProps', error);
    return {};
  }
};

export default Insight;
